import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Typography,
    IconButton,
    Badge,
    Box,
    Divider,
    Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from '../../hooks/fetchWithAuth';
import config from '../../config';
import getCookie from '../../hooks/getCookie';
import { getAvatarUrl } from '../../utils/getAvatarUrl';
import io from 'socket.io-client'; 
import './style.css';

const NotificationPanel = ({ open, onClose }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [socket, setSocket] = useState(null);
    const backendIp = config.backend_ip;
    const userId = getCookie("userId");
    const username = getCookie("username");
    const sessionId = getCookie("session_id");
    const navigate = useNavigate();

    useEffect(() => {
        if (open) {
            fetchNotifications();
        }
    }, [open]);

    useEffect(() => {
        // Conecta ao socket e entra na sala do usuário
        if (!socket && userId) {
            const socketUrl = config.socket_url;
            const newSocket = io(socketUrl, {
                transports: ['websocket'],
                reconnection: true,
                path: '/socket.io',
            });
            setSocket(newSocket);

            // Entra na sala do usuário
            newSocket.emit('join_user', { user_id: userId });

            // Escuta evento de notificação
            newSocket.on('notification_update', (data) => {
                fetchNotifications();
            });

            return () => {
                newSocket.emit('leave_user', { user_id: userId });
                newSocket.disconnect();
            };
        }
    }, [userId, socket]);

    const fetchNotifications = async () => {
        try {
            const response = await axios.get(`${backendIp}/api/notifications/list`, {
                params: {
                    user_id: userId,
                    session_id: sessionId,
                    username: username
                }
            });
            setNotifications(response.data);

            // Conta notificações não lidas
            const unread = response.data.filter(n => !n.read).length;
            setUnreadCount(unread);
        } catch (error) {
            return null;
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await axios.put(`${backendIp}/api/notifications/mark_read`, {
                notification_id: notificationId,
                session_id: sessionId,
                username: username
            });

            // Atualiza localmente
            setNotifications(prev =>
                prev.map(n =>
                    n.notification_id === notificationId
                        ? { ...n, read: true }
                        : n
                )
            );

            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            return null;
        }
    };

    const markAllAsRead = async () => {
        try {
            const unreadNotifications = notifications.filter(n => !n.read);

            await Promise.all(
                unreadNotifications.map(notification =>
                    axios.put(`${backendIp}/api/notifications/mark_read`, {
                        notification_id: notification.notification_id,
                        session_id: sessionId,
                        username: username
                    })
                )
            );

            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            return null;
        }
    };

    const deleteNotification = async (notificationId) => {
        try {
            await axios.delete(`${backendIp}/api/notifications/delete`, {
                data: {
                    notification_id: notificationId,
                    session_id: sessionId,
                    username: username
                }
            });
            setNotifications(prev =>
                prev.filter(n => n.notification_id !== notificationId)
            );
            setUnreadCount(prev =>
                prev - (notifications.find(n => n.notification_id === notificationId && !n.read) ? 1 : 0)
            );
        } catch (error) {
            return null;
        }
    };

    const formatTimeAgo = (dateString) => {
        const now = new Date();
        const date = parseDateTime(dateString);
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));

        if (diffInMinutes < 1) return 'Agora';
        if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h atrás`;

        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d atrás`;
    };

    const parseDateTime = (dateTimeString) => {
        try {
            // Formato: "11:07 08/08/2025"
            const [time, date] = dateTimeString.split(' ');
            const [hours, minutes] = time.split(':');
            const [day, month, year] = date.split('/');
            return new Date(year, month - 1, day, hours, minutes);
        } catch (error) {
            return new Date();
        }
    };

    const handleNotificationClick = (notification) => {
        // Marcar como lida se não estiver lida
        if (!notification.read) {
            markAsRead(notification.notification_id);
        }
        
        // Navegar para o cartão
        if (notification.board_id && notification.card_id) {
            navigate(`/board/${notification.board_id}/card/${notification.card_id}`);
            onClose(); // Fechar o painel de notificações
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{ zIndex: 15000 }}
            PaperProps={{
                sx: {
                    width: 380,
                    color: 'var(--text-primary, #000)'
                }
            }}
        >
            <Box className="notification-panel">
                {/* Header */}
                <Box className="notification-header">
                    <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
                        Notificações
                    </Typography>
                    {unreadCount > 0 && (
                        <Button
                            size="small"
                            onClick={markAllAsRead}
                            className="mark-all-read-btn"
                        >
                            Marcar todas como lidas
                        </Button>
                    )}
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon className="comment-preview" />
                    </IconButton>
                </Box>
                <Divider />
                {/* Lista de notificações */}
                <List className="notification-list">
                    {notifications.length === 0 ? (
                        <Box className="empty-notifications">
                            <Typography variant="body2" className='comment-preview'>
                                Nenhuma notificação encontrada
                            </Typography>
                        </Box>
                    ) : (
                        notifications.map((notification, index) => (
                            <React.Fragment key={notification.notification_id}>
                                <ListItem
                                    className={`notification-item ${!notification.read ? 'unread' : ''}`}
                                    alignItems="flex-start"
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <ListItemAvatar>
                                        <Avatar src={getAvatarUrl(notification.mentioned_by)}>
                                            <ChatBubbleOutlineIcon />
                                        </Avatar>
                                    </ListItemAvatar>

                                    <ListItemText
                                        primary={
                                            <Box className="notification-content">
                                                <div>
                                                    <Typography variant="body2" className="notification-message">
                                                        <strong>{notification.mentioned_by}</strong>
                                                    </Typography>
                                                    <Typography variant="body2" className="notification-message">
                                                        {notification.message}
                                                    </Typography>
                                                </div>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {!notification.read && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={e => { e.stopPropagation(); markAsRead(notification.notification_id); }}
                                                            className="mark-read-btn"
                                                            title="Marcar como lida"
                                                        >
                                                            <CheckIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        onClick={e => { e.stopPropagation(); deleteNotification(notification.notification_id); }}
                                                        className="delete-notification"
                                                        title="Excluir notificação"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        }
                                        secondary={
                                            <Box className="notification-details">
                                                <Typography variant="caption" className="comment-preview">
                                                    {notification.comment_preview}
                                                </Typography>
                                                <Typography variant="caption" className="notification-time">
                                                    {formatTimeAgo(notification.created_at)}
                                                </Typography>
                                            </Box>
                                        }
                                    />

                                    {!notification.read && (
                                        <Box className="unread-indicator" />
                                    )}
                                </ListItem>
                                {index < notifications.length - 1 && <Divider />}
                            </React.Fragment>
                        ))
                    )}
                </List>
            </Box>
        </Drawer>
    );
};

export default NotificationPanel;
